import { type ReactNode, useId } from "react";

import { Box, Group, Stack } from "@mantine/core";

import type { Icon as TablerIcon } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";

/**
 * One KPI card on a Practice Billing tab: a title, an optional one-line description, a large
 * value, and a status icon in the bottom-right corner, on a white surface. The card is a group
 * named by its title, so a screen reader announces the value together with what it counts.
 *
 * @param props.title - The card label.
 * @param props.description - Optional line under the title. Omit it to render the title alone.
 * @param props.value - The figure to show, already formatted (or a placeholder while loading).
 * @param props.icon - The Tabler icon named in the design.
 * @param props.iconColor - The icon stroke color, as a Mantine CSS color variable.
 * @param props.testId - The `data-testid` on the card root.
 * @returns The card element.
 */
export default function PracticeBillingSummaryCard({
  title,
  description,
  value,
  icon: Icon,
  iconColor,
  testId,
}: PracticeBillingSummaryCardProps) {
  const titleId = useId();

  return (
    <Box
      p="lg"
      bg="white"
      role="group"
      data-testid={testId}
      aria-labelledby={titleId}
      bd="1px solid var(--mantine-color-cream-4)"
      style={{ borderRadius: "var(--mantine-radius-md)" }}
      mih={description ? CARD_HEIGHT_WITH_DESCRIPTION : CARD_HEIGHT}
    >
      <Group h="100%" wrap="nowrap" align="flex-end" justify="space-between">
        <Stack gap="xs">
          <Stack gap={0}>
            <TextWrapper size="md" c="gray.7" id={titleId} variant="bold">
              {title}
            </TextWrapper>
            {description && (
              <TextWrapper size="sm" c="gray.7">
                {description}
              </TextWrapper>
            )}
          </Stack>
          <TextWrapper fz={24} lh="32px" variant="bold">
            {value}
          </TextWrapper>
        </Stack>
        <Icon aria-hidden color={iconColor} size={SUMMARY_ICON_SIZE} />
      </Group>
    </Box>
  );
}

const SUMMARY_ICON_SIZE = 24;
/** Figma card heights: 112px for a title alone, 128px when a description sits under it. */
const CARD_HEIGHT = 112;
const CARD_HEIGHT_WITH_DESCRIPTION = 128;

interface PracticeBillingSummaryCardProps {
  title: string;
  description?: string;
  value: ReactNode;
  icon: TablerIcon;
  iconColor: string;
  testId: string;
}
